import { useMemo, useEffect } from 'react';
import { normalizeLifecycleStatus, isNeedClarificationStatus, buildRegionStatusSummary } from '../../../utils/queryListUtils';
import { formatPostShortName } from '../../../services/postSelection';

/**
 * Hook to manage query filtering, pagination, and grouping for RaiseQuery page
 * @param {Object} params - Hook parameters
 * @param {Array} params.allQueries - All queries array
 * @param {string} params.selectedRegionCard - Selected region card
 * @param {string} params.selectedStatusCard - Selected status card
 * @param {string} params.tableAssignedFilter - Table assigned filter
 * @param {number} params.page - Current page
 * @param {number} params.rowsPerPage - Rows per page
 * @param {Array} params.visibleRegionOptions - Visible region options
 * @param {Function} params.setQueries - Setter for queries
 * @param {Function} params.setTotal - Setter for total
 * @param {Function} params.setStatusSummary - Setter for status summary
 * @param {Function} params.setRegionStatusSummary - Setter for region status summary
 * @param {Function} params.setPage - Setter for page
 * @returns {Object} Filtered and paginated data
 */
export const useRaiseQueryPageFilters = ({
  allQueries,
  selectedRegionCard,
  selectedStatusCard,
  tableAssignedFilter,
  page,
  rowsPerPage,
  visibleRegionOptions,
  backendCounts,
  setQueries,
  setTotal,
  setStatusSummary,
  setRegionStatusSummary,
  setPage,
}) => {
  // Filter queries by region and/or status card
  const filteredQueries = useMemo(() => {
    let filtered = allQueries;

    // Only filter by region card selection if something is selected
    if (selectedRegionCard) {
      filtered = filtered.filter((query) => {
        const queryRegion = (query.region || '').trim();
        return queryRegion === selectedRegionCard;
      });
    }

    // Then filter by status card selection (works with region filter)
    if (selectedStatusCard) {
      filtered = filtered.filter((query) => {
        const normalized = normalizeLifecycleStatus(query.statusLabel || query.status);
        const statusMap = {
          'Submitted': 'submitted',
          'Raised': 'raised',
          'Open': 'raised',
          'Delegated': 'delegated',
          'Escalated': 'escalated',
          'Closed': 'closed',
          'Need Clarification': 'additional_clarification',
        };
        const targetStatus = statusMap[selectedStatusCard];
        if (selectedStatusCard === 'Raised' || selectedStatusCard === 'Open') {
          return normalized === 'raised' || normalized === 'open';
        }
        if (selectedStatusCard === 'Need Clarification') {
          // Check both normalized and raw values to handle all cases (matching counting logic)
          const statusLabel = query.statusLabel || '';
          const status = query.status || '';
          return isNeedClarificationStatus(statusLabel, status, normalized);
        }
        return normalized === targetStatus;
      });
    }

    // Filter by table assigned filter — value is one of:
    // 'unassigned', 'post:<base short name>' (matches every query tagged
    // with that post, regardless of current assignee), or
    // 'assignee:<id>' (legacy fallback for queries with no postShortName).
    // See useRaiseQueryPageData's postOptionsMap for how options are built.
    if (tableAssignedFilter) {
      if (tableAssignedFilter === 'unassigned') {
        filtered = filtered.filter(
          (query) => !query.assignee || !(query.assignee._id || query.assignee.id)
        );
      } else if (tableAssignedFilter.startsWith('post:')) {
        const targetPostBase = tableAssignedFilter.slice('post:'.length);
        filtered = filtered.filter((query) => {
          if (!query.postShortName) return false;
          return formatPostShortName(query.postShortName).toLowerCase() === targetPostBase;
        });
      } else if (tableAssignedFilter.startsWith('assignee:')) {
        const targetAssigneeId = tableAssignedFilter.slice('assignee:'.length);
        filtered = filtered.filter((query) => {
          const assigneeId =
            query.assignee?._id ||
            query.assignee?.id ||
            (typeof query.assignee === 'string' ? query.assignee : null);
          return (
            assigneeId &&
            assigneeId.toString().toLowerCase() === targetAssigneeId.toLowerCase()
          );
        });
      }
    }

    return filtered;
  }, [allQueries, selectedRegionCard, selectedStatusCard, tableAssignedFilter]);

  // Apply client-side pagination to filtered queries
  const paginatedQueries = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredQueries.slice(startIndex, endIndex);
  }, [filteredQueries, page, rowsPerPage]);

  // Group queries by project directly (not by region)
  // Use filteredQueries (not paginated) to show all projects
  const groupedQueriesByProject = useMemo(() => {
    const projectGroups = {};

    // Group queries by project directly - use filteredQueries to show all projects
    filteredQueries.forEach((query) => {
      const projectName = query.projectName || 'Unassigned';
      if (!projectGroups[projectName]) {
        projectGroups[projectName] = {
          queries: [],
          region: (query.region || '').trim() || 'Unassigned',
        };
      }
      projectGroups[projectName].queries.push(query);
    });
    return projectGroups;
  }, [filteredQueries]);

  // Get sorted list of project names and apply pagination to projects
  const paginatedProjects = useMemo(() => {
    const projectNames = Object.keys(groupedQueriesByProject).sort((a, b) => {
      // Sort alphabetically
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });

    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return projectNames.slice(startIndex, endIndex);
  }, [groupedQueriesByProject, page, rowsPerPage]);

  // Total number of projects (for pagination)
  const totalProjects = useMemo(() => {
    return Object.keys(groupedQueriesByProject).length;
  }, [groupedQueriesByProject]);

  // Update displayed queries and status summary when region or pagination changes
  useEffect(() => {
    setQueries(paginatedQueries);
    // Calculate total projects directly from filteredQueries
    const uniqueProjects = new Set();
    filteredQueries.forEach((query) => {
      const projectName = query.projectName || 'Unassigned';
      uniqueProjects.add(projectName);
    });
    const projectCount = uniqueProjects.size;
    setTotal(projectCount); // Set total to number of projects, not queries

    // ── Region status summary: backend counts for status totals, plus a
    // client-side per-region unique-project count (allQueries, not
    // filteredQueries, so each region card always reflects its full project
    // count regardless of the currently selected region/status card) ───────
    const regionSummary = buildRegionStatusSummary(backendCounts, allQueries);
    setRegionStatusSummary(regionSummary);
    // ────────────────────────────────────────────────────────────────────────

    // Reset page if current page is beyond available data (based on projects, not queries)
    const maxPage = Math.max(0, Math.ceil(projectCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(0);
    }
  }, [allQueries, backendCounts, filteredQueries, paginatedQueries, page, rowsPerPage, setQueries, setTotal, setRegionStatusSummary, setPage]);

  return {
    filteredQueries,
    paginatedQueries,
    groupedQueriesByProject,
    paginatedProjects,
    totalProjects,
  };
};
